import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { useAuth } from '@/hooks/useAuth';
import {
  fetchMyDemandBids,
  fetchMyDemandBidDetail,
  withdrawDemandBid,
} from '@/service/api';

// 报价状态展示映射
const getBidStatusBadge = (status?: string) => {
  const normalized = (status || '').toUpperCase();
  switch (normalized) {
    case 'SUBMITTED':
    case 'PENDING':
    case 'UNDER_REVIEW':
      return {
        label: '待响应',
        badgeClass: 'bg-amber-50 text-amber-700 border-amber-200/80',
        dotClass: 'bg-amber-500',
      };
    case 'AWARDED':
    case 'ACCEPTED':
    case 'SUCCESS':
      return {
        label: '已采纳',
        badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
        dotClass: 'bg-emerald-500',
      };
    case 'REJECTED':
    case 'DECLINED':
    case 'FAILED':
      return {
        label: '未采纳',
        badgeClass: 'bg-rose-50 text-rose-700 border-rose-200/80',
        dotClass: 'bg-rose-500',
      };
    case 'WITHDRAWN':
    case 'CANCELLED':
      return {
        label: '已撤回',
        badgeClass: 'bg-slate-100 text-slate-600 border-slate-200',
        dotClass: 'bg-slate-400',
      };
    default:
      return {
        label: status || '审核中',
        badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
        dotClass: 'bg-slate-400',
      };
  }
};

export default function MyBidsPage() {
  const { user, rawUser } = useAuth();
  const residencyStatus = rawUser?.market_residency_status || user?.market_residency_status;
  const hasResidency = Boolean(residencyStatus && residencyStatus !== 'NOT_SUBMITTED');

  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [keyword, setKeyword] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [bids, setBids] = useState<Api.Demand.BidResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 撤回 Modal & 操作加载
  const [withdrawTargetId, setWithdrawTargetId] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<boolean>(false);

  // 详情 Modal
  const [detailModalBid, setDetailModalBid] = useState<Api.Demand.BidResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  const fetchKeyRef = useRef<string>('');

  const loadBids = useCallback(async () => {
    if (!hasResidency) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: Api.Demand.QueryMyDemandBidsParams = {
        page,
        page_size: pageSize,
      };
      if (activeStatus !== 'all') {
        params.status = activeStatus;
      }
      if (keyword.trim()) {
        params.keyword = keyword.trim();
      }

      const res = await fetchMyDemandBids(params);
      setBids(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('获取需求报价失败:', err);
      setError(err instanceof Error ? err.message : '获取需求报价失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, activeStatus, keyword, hasResidency]);

  useEffect(() => {
    const currentKey = `${page}-${activeStatus}-${keyword}`;
    if (fetchKeyRef.current === currentKey) return;
    fetchKeyRef.current = currentKey;
    loadBids();
  }, [page, activeStatus, keyword, loadBids]);

  // 状态 Tab 切换
  const handleTabChange = (status: string) => {
    setActiveStatus(status);
    setPage(1);
    fetchKeyRef.current = '';
  };

  // 关键词搜索
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setKeyword(searchInput);
    setPage(1);
    fetchKeyRef.current = '';
  };

  // 查看详情
  const handleViewDetail = async (bidId: string) => {
    setDetailLoading(true);
    setDetailModalBid(null);
    try {
      const res = await fetchMyDemandBidDetail(bidId);
      setDetailModalBid(res);
    } catch (err) {
      console.error('获取报价详情失败:', err);
      alert(err instanceof Error ? err.message : '获取报价详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  // 确认撤回报价
  const handleConfirmWithdraw = async () => {
    if (!withdrawTargetId || withdrawing) return;
    setWithdrawing(true);
    try {
      await withdrawDemandBid(withdrawTargetId);
      setWithdrawTargetId(null);
      fetchKeyRef.current = '';
      await loadBids();
    } catch (err) {
      console.error('撤回报价失败:', err);
      alert(err instanceof Error ? err.message : '撤回报价失败，请稍后重试');
    } finally {
      setWithdrawing(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <AccountLayout
      activePath="/my-bids"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '需求报价' }]}
    >
      {/* 头部标题与描述 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-xl text-foreground-950">我的需求报价</h1>
          <p className="text-xs text-foreground-500 mt-1">
            查看您针对平台企业需求提交的竞标报价、项目交付期与当前响应状态
          </p>
        </div>
        <Link
          to="/demands"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary-500 text-foreground-950 text-xs font-semibold hover:bg-primary-400 transition-all shadow-sm cursor-pointer shrink-0"
        >
          <i className="ri-search-line text-sm"></i>
          逛需求市场
        </Link>
      </div>

      {!hasResidency ? (
        <div className="glass-card rounded-2xl p-10 text-center border border-background-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
            <i className="ri-user-star-line text-3xl text-amber-500"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-950 mb-2">需要完善个人档案</h3>
          <p className="text-xs text-foreground-500 max-w-md mx-auto mb-6 leading-relaxed">
            查看与管理您的需求报价记录需先完成个人档案入驻。请前往个人档案页面补充您的基本信息与能力。
          </p>
          <Link
            to="/provider-profile"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-foreground-950 font-semibold text-xs hover:bg-primary-400 transition-all shadow-sm cursor-pointer"
          >
            前往个人档案
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      ) : (
        <>
          {/* 筛选选项栏与搜索 */}
          <div className="glass-card rounded-2xl p-4 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-background-200">
            <div className="flex items-center gap-1 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
              {[
                { id: 'all', label: '全部' },
                { id: 'SUBMITTED', label: '待响应' },
                { id: 'AWARDED', label: '已采纳' },
                { id: 'REJECTED', label: '未采纳' },
                { id: 'WITHDRAWN', label: '已撤回' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${activeStatus === tab.id
                      ? 'bg-primary-500 text-foreground-950 font-semibold shadow-xs'
                      : 'text-foreground-600 hover:bg-background-100'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="relative flex items-center min-w-[240px]">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="搜索需求标题关键词..."
                className="w-full pl-9 pr-14 py-2 rounded-xl border border-background-300 bg-white text-xs text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <i className="ri-search-line absolute left-3 text-foreground-400 text-sm"></i>
              <button
                type="submit"
                className="absolute right-1 px-3 py-1 bg-primary-500 text-foreground-950 text-xs font-semibold rounded-lg hover:bg-primary-400 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          {/* 列表主体 */}
          {loading ? (
            <div className="glass-card rounded-2xl p-12 text-center border border-background-200">
              <div className="inline-block w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs text-foreground-400 mt-3">正在加载我的需求报价...</p>
            </div>
          ) : error ? (
            <div className="glass-card rounded-2xl p-8 text-center border border-red-200 bg-red-50/50">
              <i className="ri-error-warning-line text-3xl text-rose-500 mb-2 block"></i>
              <p className="text-sm font-semibold text-rose-700 mb-2">{error}</p>
              <button
                onClick={() => {
                  fetchKeyRef.current = '';
                  loadBids();
                }}
                className="px-4 py-1.5 bg-rose-600 text-white text-xs font-medium rounded-lg hover:bg-rose-500 transition-colors cursor-pointer"
              >
                重试
              </button>
            </div>
          ) : bids.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center border border-background-200">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-800 mb-1">暂无需求报价记录</h3>
              <p className="text-xs text-foreground-400 max-w-sm mx-auto mb-6">
                您尚未提交过该状态的需求报价。去大厅浏览最新的企业发包意向，挑选适合的项目吧！
              </p>
              <Link
                to="/demands"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-foreground-950 text-xs font-semibold hover:bg-primary-400 transition-all cursor-pointer shadow-sm"
              >
                <i className="ri-compass-3-line text-sm"></i>
                探索供需大厅
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bids.map((bid) => {
                const statusInfo = getBidStatusBadge(bid.bid_status);
                const isCanWithdraw =
                  bid.can_withdraw !== false &&
                  ((bid.bid_status || '').toUpperCase() === 'SUBMITTED' ||
                    (bid.bid_status || '').toUpperCase() === 'PENDING');

                return (
                  <div
                    key={bid.id}
                    className="glass-card rounded-2xl p-5 border border-background-200/80 hover:border-primary-300/80 transition-all hover:shadow-md group"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 pb-4 border-b border-background-200/60">
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusInfo.badgeClass}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dotClass}`}></span>
                            {statusInfo.label}
                          </span>
                          {bid.enterprise_name_masked && (
                            <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-md flex items-center gap-1">
                              <i className="ri-building-line text-foreground-400"></i>
                              {bid.enterprise_name_masked}
                            </span>
                          )}
                        </div>
                        <h2 className="text-base font-bold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-1">
                          {bid.demand_id ? (
                            <Link to={`/demands/${bid.demand_id}`} className="hover:underline">
                              {bid.demand_title || '未命名需求'}
                            </Link>
                          ) : (
                            bid.demand_title || '未命名需求'
                          )}
                        </h2>
                      </div>

                      {/* 报价金额与天数卡片 */}
                      <div className="flex items-baseline gap-3 shrink-0 bg-primary-50/50 px-4 py-2 rounded-xl border border-primary-100">
                        <div>
                          <span className="text-[10px] text-foreground-400 block">我的报价</span>
                          <span className="text-lg font-extrabold text-primary-700">
                            ¥ {bid.quoted_amount ? Number(bid.quoted_amount).toLocaleString() : '--'}
                          </span>
                        </div>
                        {bid.expected_delivery_days !== undefined && (
                          <div className="pl-3 border-l border-primary-200">
                            <span className="text-[10px] text-foreground-400 block">交付周期</span>
                            <span className="text-xs font-semibold text-foreground-800">
                              {bid.expected_delivery_days} 天
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 竞标留言摘要 */}
                    {bid.message && (
                      <div className="mt-3.5 p-3 rounded-xl bg-background-50 border border-background-200/50 text-xs text-foreground-700">
                        <span className="font-semibold text-foreground-900 mr-1.5">竞标说明:</span>
                        <span className="line-clamp-2 text-foreground-600">{bid.message}</span>
                      </div>
                    )}

                    {/* 底部时间与操作 */}
                    <div className="mt-4 pt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-foreground-400">
                      <div className="flex items-center gap-4 flex-wrap">
                        {bid.created_at && (
                          <span>提交时间：{new Date(bid.created_at).toLocaleString()}</span>
                        )}
                        {bid.bid_deadline_at && (
                          <span>截止时间：{new Date(bid.bid_deadline_at).toLocaleDateString()}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {isCanWithdraw && (
                          <button
                            type="button"
                            onClick={() => setWithdrawTargetId(bid.id || null)}
                            className="px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 font-medium transition-colors cursor-pointer"
                          >
                            撤回报价
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => bid.id && handleViewDetail(bid.id)}
                          className="px-3.5 py-1.5 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-700 text-foreground-700 font-medium transition-colors cursor-pointer"
                        >
                          详情节点
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 分页控制 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                disabled={page <= 1}
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                  fetchKeyRef.current = '';
                }}
                className="px-3 py-1.5 rounded-lg border border-background-300 bg-white text-xs font-medium text-foreground-700 hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                上一页
              </button>
              <span className="text-xs text-foreground-500 font-medium px-2">
                {page} / {totalPages} 页
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => {
                  setPage((p) => Math.min(totalPages, p + 1));
                  fetchKeyRef.current = '';
                }}
                className="px-3 py-1.5 rounded-lg border border-background-300 bg-white text-xs font-medium text-foreground-700 hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                下一页
              </button>
            </div>
          )}

          {/* 报价撤回二次确认 Modal */}
          {withdrawTargetId && (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4 animate-[fadeIn_0.15s_ease-out]">
                <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto text-xl">
                  <i className="ri-error-warning-line"></i>
                </div>
                <div className="text-center">
                  <h3 className="text-base font-bold text-foreground-900">确认撤回此需求报价？</h3>
                  <p className="text-xs text-foreground-500 mt-1.5">
                    撤回后需求企业将无法查看到该报价，如有意向需重新提交。
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    disabled={withdrawing}
                    onClick={() => setWithdrawTargetId(null)}
                    className="flex-1 py-2.5 rounded-xl border border-background-300 text-foreground-600 text-xs font-semibold hover:bg-background-100 cursor-pointer"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    disabled={withdrawing}
                    onClick={handleConfirmWithdraw}
                    className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white text-xs font-semibold hover:bg-rose-500 disabled:opacity-50 cursor-pointer"
                  >
                    {withdrawing ? '撤回中...' : '确认撤回'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 报价详情 Modal */}
          {(detailModalBid || detailLoading) && (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 my-8 animate-[fadeIn_0.15s_ease-out]">
                <div className="flex items-center justify-between pb-3 border-b border-background-200">
                  <h3 className="text-base font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-primary-600"></i>
                    报价详情记录
                  </h3>
                  <button
                    type="button"
                    onClick={() => setDetailModalBid(null)}
                    className="text-foreground-400 hover:text-foreground-700 text-lg cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                {detailLoading ? (
                  <div className="py-8 text-center">
                    <div className="inline-block w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-xs text-foreground-400 mt-2">获取报价详情中...</p>
                  </div>
                ) : detailModalBid ? (
                  <div className="space-y-4 text-xs text-foreground-700">
                    {/* 需求基本信息 */}
                    <div className="p-3.5 rounded-xl bg-background-50 border border-background-200">
                      <p className="text-[10px] text-foreground-400 font-medium uppercase tracking-wider mb-1">
                        目标需求
                      </p>
                      <p className="font-bold text-sm text-foreground-950">{detailModalBid.demand_title || '--'}</p>
                      {detailModalBid.enterprise_name_masked && (
                        <p className="text-xs text-foreground-500 mt-1">
                          需求方企业：{detailModalBid.enterprise_name_masked}
                        </p>
                      )}
                    </div>

                    {/* 报价数据概览 */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-primary-50/60 border border-primary-100">
                        <span className="text-foreground-400 text-[10px] block">报价金额</span>
                        <span className="text-lg font-bold text-primary-700">
                          ¥ {detailModalBid.quoted_amount ? Number(detailModalBid.quoted_amount).toLocaleString() : '--'}
                        </span>
                      </div>
                      <div className="p-3 rounded-xl bg-background-50 border border-background-200">
                        <span className="text-foreground-400 text-[10px] block">预计交付时间</span>
                        <span className="text-sm font-semibold text-foreground-900 mt-0.5 block">
                          {detailModalBid.expected_delivery_days ? `${detailModalBid.expected_delivery_days} 天` : '未指定'}
                        </span>
                      </div>
                    </div>

                    {/* 竞标留言 */}
                    {detailModalBid.message && (
                      <div>
                        <h4 className="font-semibold text-foreground-900 mb-1">竞标留言 / 方案说明</h4>
                        <p className="p-3 rounded-xl bg-background-50 border border-background-200/70 text-foreground-700 leading-relaxed whitespace-pre-wrap">
                          {detailModalBid.message}
                        </p>
                      </div>
                    )}

                    {/* 招募要求 */}
                    {detailModalBid.undertaking_requirements && detailModalBid.undertaking_requirements.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-foreground-900 mb-1">承接要求</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {detailModalBid.undertaking_requirements.map((req, idx) => (
                            <span key={idx} className="px-2.5 py-1 rounded-lg bg-background-100 text-foreground-700 text-xs">
                              {req}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 报价节点时间线 */}
                    {detailModalBid.timeline && detailModalBid.timeline.length > 0 && (
                      <div className="pt-2">
                        <h4 className="font-semibold text-foreground-900 mb-2">事件时间线</h4>
                        <div className="space-y-3 pl-2 border-l-2 border-primary-200">
                          {detailModalBid.timeline.map((event, i) => (
                            <div key={event.id || i} className="relative pl-4">
                              <div className="absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full bg-primary-500 border-2 border-white"></div>
                              <p className="font-medium text-foreground-800">
                                {event.event_type || '状态更新'}
                                {event.status_to ? ` -> ${event.status_to}` : ''}
                              </p>
                              {event.occurred_at && (
                                <p className="text-[10px] text-foreground-400 mt-0.5">
                                  {new Date(event.occurred_at).toLocaleString()}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="pt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDetailModalBid(null)}
                    className="px-5 py-2 rounded-xl bg-background-200 hover:bg-background-300 text-foreground-800 text-xs font-semibold transition-colors cursor-pointer"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          )}

        </>
      )}
    </AccountLayout>
  );
}
